import { useMemo, useState } from 'react'

import clsx from 'clsx'

import { useSiteDashboardGetApi } from '@/common/api/modules/site'
import { SiteDashboardResponse } from '@/common/api/params/site'
import { w2ProductCategoriesPath, w2ProductsPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { handleSmoothScroll } from '@/common/util/scroll'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content, { ContentType } from '@/components/view/layouts/page/Content'
import ContentSideFrame from '@/components/view/layouts/page/ContentSideFrame'
import Main from '@/components/view/layouts/page/Main'
import Side from '@/components/view/layouts/page/Side'
import PageEditLoading from '@/components/view/molecules/loading/PageEditLoading'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import MainInnerLoader from '@/components/container/MainInnerLoader'
import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import W2ModeMessage from '@/components/container/modules/master/master/W2ModeMessage'

const PHASES = [
  {
    key: 'content',
    label: 'STEP 01',
    title: 'コンテンツ準備',
    desc: 'レコメンドに必要なマスタデータ・受注データを登録します'
  },
  {
    key: 'tags',
    label: 'STEP 02',
    title: '配信タグ設置',
    desc: 'ECサイトの各ページにトラッキングタグを設置します'
  },
  {
    key: 'widget',
    label: 'STEP 03',
    title: 'レコメンドパーツ・配信作成',
    desc: 'レコメンド表示用のレコメンドパーツ/配信を作成し、タグを埋め込みます'
  },
  {
    key: 'verify',
    label: 'STEP 04',
    title: 'データ取得確認',
    desc: 'タグから正しくデータが送信されているか確認します'
  },
  {
    key: 'analysis',
    label: 'STEP 05',
    title: '分析・活用',
    desc: '取得データを使って顧客分析やジャーニー分析を行います'
  }
] as const

type PhaseKey = (typeof PHASES)[number]['key']

interface Step {
  phase: PhaseKey
  label: string
  desc: string
  done: boolean
  link: string
  linkLabel?: string
  count?: number
  countLabel?: string
  isW2?: boolean
}

const SetupGuideProgress = ({ steps }: { steps: Step[] }) => {
  const doneCount = steps.filter((s) => s.done).length

  return (
    <Area>
      <Heading>全体進捗</Heading>
      <AreaContent>
        <div className='flex flex-wrap gap-1.5'>
          <div className='ml-auto flex items-center justify-end gap-1'>
            <Text size={20} weight='bold' color={TextColor.PRIMARY}>
              {doneCount}
            </Text>
            <Text lineHeight={150}>/{steps.length} 完了</Text>
          </div>
          {PHASES.map((phase) => {
            const phaseSteps = steps.filter((s) => s.phase === phase.key)
            const phaseDone = phaseSteps.filter((s) => s.done).length
            const allDone = phaseDone === phaseSteps.length && phaseSteps.length > 0
            return (
              <a
                key={phase.key}
                href={`#phase-${phase.key}`}
                className={clsx(
                  'inline-flex w-full items-center gap-2 rounded-xl px-4 py-3 no-underline transition-all',
                  allDone ? 'bg-surface-disabled' : 'border-secondary border'
                )}
                onClick={handleSmoothScroll}
              >
                <Badge
                  badgeType={allDone ? BadgeType.SQUARE_DISABLED : BadgeType.SQUARE_BRAND}
                  size={12}
                >
                  {phase.label}
                </Badge>
                <Text weight='bold' color={allDone ? TextColor.DISABLED : TextColor.PRIMARY}>
                  {phase.title}
                </Text>
                <div className='ml-auto'>
                  {allDone ? (
                    <Badge badgeType={BadgeType.SUCCESS} size={10}>
                      すべて完了
                    </Badge>
                  ) : (
                    <Text color={TextColor.SECONDARY}>
                      {phaseDone}/{phaseSteps.length}
                    </Text>
                  )}
                </div>
              </a>
            )
          })}
        </div>
      </AreaContent>
    </Area>
  )
}

const buildBaseSteps = (data: SiteDashboardResponse, siteId: number): Step[] => {
  const bt = data.behavior_by_type ?? {}
  return [
    {
      phase: 'content',
      label: '商品マスタの登録',
      desc: 'ECサイトの商品情報をCSV等で登録します。レコメンドの商品表示に必要です。',
      done: data.products > 0,
      link: w2ProductsPath(siteId),
      count: data.products,
      countLabel: '件登録済み'
    },
    {
      phase: 'content',
      label: 'カテゴリマスタの登録',
      desc: '商品カテゴリを登録します。カテゴリ別レコメンドに使用します。',
      done: data.categories > 0,
      link: w2ProductCategoriesPath(siteId),
      count: data.categories,
      countLabel: '件登録済み'
    },
    {
      phase: 'content',
      label: '受注履歴データの取込み',
      desc: '過去の受注データを取り込みます。BB（この商品を買った人は〜）ロジックに必要です。',
      done: data.orders > 0,
      link: `/sites/${siteId}/recommend/order-history`,
      count: data.orders,
      countLabel: '件取込済み'
    },
    {
      phase: 'tags',
      label: '基本タグの設置（全ページ共通）',
      desc: 'ECサイト全ページの<head>にJS読込タグを設置します。全てのトラッキングの基盤となるタグです。',
      done:
        (bt['view'] ?? 0) + (bt['view_product'] ?? 0) + (bt['cart'] ?? 0) + (bt['order'] ?? 0) > 0,
      link: `/sites/${siteId}/recommend/tags`,
      linkLabel: 'タグを確認'
    },
    {
      phase: 'tags',
      label: '閲覧タグの設置（商品詳細ページ）',
      desc: '商品詳細ページに設置し、どの商品が閲覧されたかを記録します。OB・similarロジックに必要です。',
      done: (bt['view_product'] ?? 0) > 0,
      link: `/sites/${siteId}/recommend/tags`,
      linkLabel: 'タグを確認',
      count: bt['view_product'] ?? 0,
      countLabel: '件の閲覧記録'
    },
    {
      phase: 'tags',
      label: '閲覧タグの設置（カテゴリページ）',
      desc: 'カテゴリ一覧ページに設置し、閲覧カテゴリを記録します。',
      done: (bt['view'] ?? 0) > 0,
      link: `/sites/${siteId}/recommend/tags`,
      linkLabel: 'タグを確認',
      count: bt['view'] ?? 0,
      countLabel: '件の閲覧記録'
    },
    {
      phase: 'tags',
      label: 'カートタグの設置（カートページ）',
      desc: 'カートページに設置し、カート追加イベントを記録します。ファネル分析に必要です。',
      done: (bt['cart'] ?? 0) > 0,
      link: `/sites/${siteId}/recommend/tags`,
      linkLabel: 'タグを確認',
      count: bt['cart'] ?? 0,
      countLabel: '件のカート記録'
    },
    {
      phase: 'tags',
      label: '購入完了タグの設置（注文完了ページ）',
      desc: '注文完了ページに設置し、コンバージョンを記録します。CVR計測に必須です。',
      done: (bt['order'] ?? 0) > 0,
      link: `/sites/${siteId}/recommend/tags`,
      linkLabel: 'タグを確認',
      count: bt['order'] ?? 0,
      countLabel: '件の購入記録'
    },
    {
      phase: 'widget',
      label: 'レコメンドパーツの作成',
      desc: 'レコメンドパーツを作成します。表示ロジック、レイアウト、CSSを設定します。',
      done: data.widgets > 0,
      link: `/sites/${siteId}/recommend/widgets`,
      count: data.widgets,
      countLabel: '個作成済み'
    },
    {
      phase: 'widget',
      label: 'レコメンド表示用タグの埋め込み',
      desc: '配信新規作成でレコメンドパーツを選択して配信を作成後、配信の埋め込みコードをECサイトのレコメンド表示箇所に埋め込みます。',
      done: (data.recommendation_logs ?? 0) > 0,
      // 表示コードのタブへ直接飛ばす（既定のトラッキングコードのタブではない）
      link: `/sites/${siteId}/recommend/tags?tab=display`,
      linkLabel: 'タグを確認'
    },
    {
      phase: 'verify',
      label: '行動データの取得確認',
      desc: 'タグ設置後、行動履歴ページで閲覧・カート・購入イベントが正しく記録されているか確認します。',
      done: data.customers_total > 0,
      link: `/sites/${siteId}/recommend/behavior`,
      linkLabel: '行動履歴を確認',
      count: data.month_behavior ?? 0,
      countLabel: '件（今月）'
    },
    {
      phase: 'verify',
      label: '配信履歴の確認',
      desc: 'ウィジェット経由でレコメンドが配信されているか確認します。表示回数・クリック数を確認できます。',
      done: (data.recommendation_logs ?? 0) > 0,
      link: `/sites/${siteId}/recommend/dashboard/delivery-stats`,
      linkLabel: '配信履歴を確認',
      count: data.recommendation_logs ?? 0,
      countLabel: '件の配信記録'
    },
    {
      phase: 'analysis',
      label: '配信内容の最適化（A/Bテスト）',
      desc: 'ウィジェットのA/Bテスト機能でレコメンドロジックを比較し、最適な配信設定を見つけます。',
      done: data.widgets > 0 && (data.recommendation_logs ?? 0) > 0,
      link: `/sites/${siteId}/ab-campaigns`,
      linkLabel: '配信内容決定へ'
    }
  ]
}

// W2マスタの種類と日本語ラベルの対応（初期設定ガイドの取込み進捗判定で使用）
const W2_MASTER_TYPES: { label: string; masterType: string }[] = [
  { label: 'ユーザーマスタ', masterType: 'w2_User' },
  { label: 'ユーザー属性', masterType: 'w2_UserAttribute' },
  { label: '商品マスタ', masterType: 'w2_Product' },
  { label: '商品ブランド', masterType: 'w2_ProductBrand' },
  { label: '商品カテゴリ', masterType: 'w2_ProductCategory' },
  { label: '商品タグ', masterType: 'w2_ProductTag' },
  { label: '商品在庫', masterType: 'w2_ProductStock' },
  { label: '受注明細', masterType: 'w2_OrderItem' },
  { label: '特集ページ', masterType: 'w2_FeaturePage' },
  { label: '特集ページカテゴリ', masterType: 'w2_FeaturePageCategory' },
  { label: '特集ページコンテンツ', masterType: 'w2_FeaturePageContents' },
  { label: '商品グループ', masterType: 'w2_ProductGroup' },
  { label: '商品グループ明細', masterType: 'w2_ProductGroupItem' }
]

const formatRetrainAt = (iso: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const buildW2Steps = (
  siteId: number,
  w2MasterCounts: Record<string, number>,
  retrainLastAt: string | null
): Step[] => {
  // W2マスタ取込み: 1件でもデータがあれば完了。空のマスタは留意点として併記
  const importedLabels = W2_MASTER_TYPES.filter((t) => (w2MasterCounts[t.masterType] ?? 0) > 0).map(
    (t) => t.label
  )
  const missingLabels = W2_MASTER_TYPES.filter(
    (t) => (w2MasterCounts[t.masterType] ?? 0) === 0
  ).map((t) => t.label)
  const importDone = importedLabels.length > 0
  const importDesc = importDone
    ? `以下のW2マスタが取り込まれています：${importedLabels.join('、')}${
        missingLabels.length > 0
          ? `\n※ 未取込のマスタ（必要に応じて取込みをご検討ください）：${missingLabels.join('、')}`
          : ''
      }`
    : '以下のW2マスタをCSVまたはFTPで取り込んでください：ユーザーマスタ、ユーザー属性、商品マスタ、商品ブランド、商品カテゴリ、商品タグ、商品在庫、受注明細、特集ページ、特集ページカテゴリ、特集ページコンテンツ、商品グループ、商品グループ明細'

  // W2商品在庫
  const stockCount = w2MasterCounts['w2_ProductStock'] ?? 0
  const stockDone = stockCount > 0

  // モデルチューニング: 実施履歴があれば完了
  const retrainDone = !!retrainLastAt
  const retrainDesc = retrainDone
    ? `W2の14マスタデータがモデル学習に自動で活用されます。最新の実施日時: ${formatRetrainAt(retrainLastAt)}`
    : 'W2の14マスタデータがモデル学習に自動で活用されます。モデルチューニング画面で確認できます。'

  return [
    {
      phase: 'content',
      label: 'W2マスタデータの取込み',
      desc: importDesc,
      done: importDone,
      link: `/sites/${siteId}/file-upload`,
      linkLabel: 'ファイル取込みへ',
      isW2: true
    },
    {
      phase: 'content',
      label: 'W2商品在庫データの登録',
      desc: 'バリエーション別の在庫データを取り込むことで、全バリエーション在庫0の商品をレコメンドから自動除外します。',
      done: stockDone,
      link: `/sites/${siteId}/w2-product-inventory`,
      linkLabel: 'W2在庫データへ',
      isW2: true
    },
    {
      phase: 'analysis',
      label: 'W2マスタ連動によるモデルチューニング',
      desc: retrainDesc,
      done: retrainDone,
      link: `/sites/${siteId}/recommend/model-tuning`,
      linkLabel: 'モデルチューニングへ',
      isW2: true
    }
  ]
}

interface StepCardProps {
  step: Step
  disabled: boolean
}

const StepCard = ({ step, disabled }: StepCardProps) => (
  <div
    className={clsx(
      'rounded-xl p-4',
      step.done || disabled
        ? 'bg-surface-disabled'
        : step.isW2
          ? 'bg-surface border-brand border'
          : 'bg-surface border-secondary border'
    )}
  >
    <div className='flex items-start gap-3'>
      <Icon
        type={IconType.CIRCLE_CHECK}
        color={disabled ? IconColor.DISABLED : step.done ? IconColor.GREEN : IconColor.SECONDARY}
        size={20}
      />
      <div className='min-w-0 flex-1'>
        <div className='flex flex-wrap items-center gap-2'>
          <Text
            size={14}
            weight={'bold'}
            color={step.done || disabled ? TextColor.DISABLED : TextColor.PRIMARY}
          >
            {step.label}
          </Text>
          {step.isW2 && (
            <Badge badgeType={BadgeType.SQUARE_BRAND_SECONDARY} size={12}>
              W2完全連動
            </Badge>
          )}
          {step.done && (
            <Badge badgeType={BadgeType.SUCCESS} size={10}>
              完了
            </Badge>
          )}
        </div>
        <div className='mt-1'>
          <Text
            size={12}
            color={step.done || disabled ? TextColor.DISABLED : TextColor.PRIMARY}
            preWrap
          >
            {step.desc}
          </Text>
        </div>
      </div>
      <div className='ml-2 flex shrink-0 items-center gap-2'>
        {step.count != null && step.count > 0 && (
          <Badge badgeType={BadgeType.SQUARE_SECONDARY} size={12}>
            {step.count.toLocaleString()} {step.countLabel}
          </Badge>
        )}
        {!disabled && !step.done && (
          <Button link={step.link} size='base'>
            {step.linkLabel ?? '設定する'}
          </Button>
        )}
      </div>
    </div>
  </div>
)

interface SetupGuideAreaProps {
  phase: (typeof PHASES)[number]
  phaseSteps: Step[]
}

const SetupGuideArea = ({ phase, phaseSteps }: SetupGuideAreaProps) => {
  const phaseDone = phaseSteps.filter((s) => s.done).length
  const allDone = phaseDone === phaseSteps.length && phaseSteps.length > 0
  const disabled = allDone

  const [isOpening, setIsOpening] = useState(!allDone)

  return (
    <Area key={phase.key} id={`phase-${phase.key}`}>
      <Heading
        onOpenCloseClick={() => setIsOpening((isOpening) => !isOpening)}
        isOpening={isOpening}
        rightNode={
          <Text size={14} color={TextColor.SECONDARY}>
            {phaseDone}/{phaseSteps.length}
          </Text>
        }
        color={disabled ? TextColor.DISABLED : TextColor.PRIMARY}
      >
        <ItemAlign gapX={8} align='center'>
          <Badge badgeType={disabled ? BadgeType.SQUARE_DISABLED : BadgeType.SQUARE_BRAND}>
            {phase.label}
          </Badge>
          {phase.title}
          <span className='ml-2' />
          {allDone && (
            <Badge badgeType={BadgeType.SUCCESS} size={10}>
              すべて完了
            </Badge>
          )}
        </ItemAlign>
        <Text size={12}>{phase.desc}</Text>
      </Heading>
      <AreaContent isOpening={isOpening}>
        <div className='flex flex-col gap-2'>
          {phaseSteps.map((step, i) => (
            <StepCard key={i} step={step} disabled={disabled} />
          ))}
        </div>
      </AreaContent>
    </Area>
  )
}

const SetupGuidePage = () => {
  const siteId = useSiteId()

  // include_setup: この画面だけが使う重い集計（W2マスタ種別ごとの件数・レコメンド配信数・
  // 行動タイプ別件数・マッピング件数）を要求する。付けないと 0 / 空で返る
  const { data: dashboardData, error: dashboardError } = useSiteDashboardGetApi({
    site_id: siteId,
    include_setup: true
  })
  const baseSteps = useMemo(
    () => (dashboardData ? buildBaseSteps(dashboardData, siteId) : []),
    [dashboardData, siteId]
  )

  const w2Steps = useMemo(
    () =>
      buildW2Steps(
        siteId,
        dashboardData?.w2_master_counts ?? {},
        dashboardData?.retrain_last_at ?? null
      ),
    [siteId, dashboardData?.w2_master_counts, dashboardData?.retrain_last_at]
  )

  const steps = useMemo<Step[]>(() => {
    const result: Step[] = []
    let w2ContentAdded = false
    let w2AnalysisAdded = false

    for (const step of baseSteps) {
      result.push(step)

      if (step.phase === 'content' && !w2ContentAdded) {
        const nextStep = baseSteps[baseSteps.indexOf(step) + 1]
        if (!nextStep || nextStep.phase !== 'content') {
          w2Steps.filter((s) => s.phase === 'content').forEach((s) => result.push(s))
          w2ContentAdded = true
        }
      }

      if (step.phase === 'analysis' && !w2AnalysisAdded) {
        const nextStep = baseSteps[baseSteps.indexOf(step) + 1]
        if (!nextStep || nextStep.phase !== 'analysis') {
          w2Steps.filter((s) => s.phase === 'analysis').forEach((s) => result.push(s))
          w2AnalysisAdded = true
        }
      }
    }

    return result
  }, [baseSteps, w2Steps])

  return (
    <Main>
      <PageTitle
        title='設定ガイド'
        badgeNode={<AppTitleBadge type='recommend' />}
        description={dashboardData ? `${dashboardData.site_name} のセットアップ状況` : undefined}
      />
      <MainInnerLoader error={dashboardError} data={dashboardData} withSide>
        {dashboardData && (
          <div className='flex flex-col gap-6 pb-6'>
            {/* 全サイトが W2Commerce 完全連動。連動していることの案内は常に出す */}
            <W2ModeMessage
              message='W2Commerceの14マスタデータと連動し、より精度の高いレコメンドを実現します。
              配信タグには item_variation_id と brand_id が追加され、バリエーション・ブランド別のトラッキングが可能になります。'
            />
            {/* 全体進捗 */}
            <ContentSideFrame>
              <Content type={ContentType.TWO_COLUMNS_LEFT}>
                {PHASES.map((phase) => (
                  <SetupGuideArea
                    key={phase.key}
                    phase={phase}
                    phaseSteps={steps.filter((s) => s.phase === phase.key)}
                  />
                ))}
              </Content>
              <Side follow>
                <SetupGuideProgress steps={steps} />
              </Side>
            </ContentSideFrame>
          </div>
        )}
      </MainInnerLoader>
    </Main>
  )
}

export default SetupGuidePage
